import { Component, ElementRef, Input, ViewChild } from "@angular/core";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { CardInDeck, GameType, GetDecksWithCardResponse, MagicCard } from "@apiserver-rest/ponder-data-client-base";
import { getDeckRoute } from "@ponder-features/user/decks/deck-router";
import { BusySpinComponent } from "@ponder-shared/components/busy-spin.component";
import { Subscription, take } from "rxjs";
import { getMagicCardRoute2 } from "./magicCardRouter";
import { environment } from "../../../../environments/environment";
import { AddCardToDeckModalComponent } from "@ponder-features/user/decks/add-card-to-deck-modal.component";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { GenericErrorComponent } from "@ponder-shared/components/errors/generic-error.component";
import { IndirectDeckCardUpdatedEvent } from "@ponder-core/events/deck.events";
import { MagicCardInDecksRowComponent as MagicCardInDecksRowComponent } from "./magic-card-in-decks-row.component";
import { NavigationService } from "@ponder-core/navigation/navigation.service";

@Component({
    selector: "magic-card-in-decks",
    imports: [BusySpinComponent, GenericErrorComponent, AddCardToDeckModalComponent, MagicCardInDecksRowComponent],
    templateUrl: "./magic-card-in-decks.component.html"
})
export class MagicCardInDecksComponent {
    @Input({ required: true }) public username!: string;
    @Input({ required: true }) public magicCard!: MagicCard;
    
    @ViewChild("versiontooltip") private versionTooltipRef!: ElementRef<HTMLElement>;

    public isLoading: boolean = true;
    public loadingSuccess: boolean = false;
    public decksWithCard: DeckWithCard[] = [];
    public hoverTarget?: MatchingCardInDeck;

    private indirectUpdateSub!: Subscription;

    public constructor(private events: EventBroker, private ponderDataClient: PonderDataClient, private navigation: NavigationService) {
        this.indirectUpdateSub = this.events.deck.indirectDeckCardUpdatedEvent.subscribe({
            next: (event: IndirectDeckCardUpdatedEvent) => {
                this.onDeckCardCountAdjusted(event.deckId, event.deckName, event.deckColor, event.backgroundCardId, event.layer, event.count, event.specialCount);
            }
        });
    }

    public ngOnInit(): void {
        this.fetchedDecksWithCard();
    }

    public ngOnDestroy(): void {
        this.indirectUpdateSub.unsubscribe();
    }

    private fetchedDecksWithCard(): void {
        this.ponderDataClient
            .decksWithCard(GameType.Magic, this.magicCard.name!)
            .pipe(take(1))
            .subscribe({
                next: (response: GetDecksWithCardResponse) => {
                    if (response.success) {
                        this.loadingSuccess = true;

                        response.cards?.forEach(card => {
                            var deck = this.getOrCreateDeck(card.deckId, card.deckName!, card.deckColor!, card.backgroundCardId);
                            deck.addCard(card);
                        });

                        this.decksWithCard.sort((a, b) => a.deckName.localeCompare(b.deckName));
                        this.decksWithCard.forEach(d => {
                            d.layers.forEach(l => {
                                l.cards.sort((a, b) => a.setIndex.localeCompare(b.setIndex));
                            });
                        });
                    }
                }
            })
            .add(() => { this.isLoading = false; });
    }

    private getOrCreateDeck(deckId: string, deckName: string, deckColor: string, backgroundCardId: string | undefined): DeckWithCard {
        for (let i = 0; i < this.decksWithCard.length; ++i) {
            if (this.decksWithCard[i].deckId == deckId) {
                return this.decksWithCard[i];
            }
        }

        var deck = new DeckWithCard(deckId, deckName, deckColor, backgroundCardId);
        this.decksWithCard.push(deck);

        return deck;
    }

    public isVisible(deck: DeckWithCard): boolean {
        for (let i = 0; i < deck.layers.length; ++i) {
            if (deck.layers[i].cards && deck.layers[i].cards.length) {
                for (let j = 0; j < deck.layers[i].cards.length; ++j) {
                    if (deck.layers[i].cards[j].count || deck.layers[i].cards[j].specialCount) {
                        return true;
                    }
                }
            }
        }

        return false;
    }

    public onDeckClick(deck: DeckWithCard): void {
        this.navigation.goto(getDeckRoute(this.username, deck.deckId));
    }

    public onCardClick(card: MatchingCardInDeck): void {
        this.navigation.goto(getMagicCardRoute2(card.cardName, card.setCode, card.setIndex));
    }

    public isThisCard(card: MatchingCardInDeck): boolean {
        return this.magicCard.id == card.itemId;
    }

    public onVersionHover(hoveredCard: MatchingCardInDeck): void {
        this.hoverTarget = hoveredCard;
        this.versionTooltipRef.nativeElement.style.setProperty("background-image", `url("${environment.ponderImageBaseUrl}/card/small/front/${this.hoverTarget.itemId}.jpg")`);
        this.versionTooltipRef.nativeElement.classList.remove("invisible"); 
    }

    public onVersionStopHover(): void {
        this.hoverTarget = undefined;
        this.versionTooltipRef.nativeElement.classList.add("invisible");
    }

    public onAddVersionToDeckClick(): void {
        this.events.deck.showAddCardToDecksModalEvent.emit({
            game: GameType.Magic,
            name: this.magicCard.name!,
            itemId: this.magicCard.id!,
            hasNormal: this.magicCard.treatment.normal,
            hasSpecial: this.magicCard.treatment.special
        });
    }

    /**
     * Note: We don't need to pass the actual card because the actual card is us!
     * This component only appears on a specific card page.
     * 
     * @param deckId
     * @param deckName 
     * @param layerName 
     */
    private onDeckCardCountAdjusted(deckId: string, deckName: string, deckColor: string, backgroundCardId: string | undefined, layerName: string, count: number, specialCount: number): void {
        var deck = this.getOrCreateDeck(deckId, deckName, deckColor, backgroundCardId);
        var layer = deck.getOrCreateLayer(layerName);

        layer.updateCard(
            this.magicCard.id!,
            this.magicCard.name!,
            this.magicCard.set!.name!,
            this.magicCard.set!.code!,
            this.magicCard.setIndex!,
            count,
            specialCount
        );
    }
}

export class DeckWithCard {
    public layers: LayerWithCard[] = [];

    public constructor(public deckId: string, public deckName: string, public deckColor: string, public backgroundCardId: string | undefined) {

    }

    public addCard(card: CardInDeck): void {
        var layer = this.getOrCreateLayer(card.layer!);
        layer.addCard(card);
    }

    public getOrCreateLayer(name: string): LayerWithCard {
        for (let i = 0; i < this.layers.length; ++i) {
            if (this.layers[i].layerName == name) {
                return this.layers[i];
            }
        }

        var layer = new LayerWithCard(name);
        this.layers.push(layer);

        return layer;
    }
};

export class LayerWithCard {
    public cards: MatchingCardInDeck[] = [];

    public constructor(public layerName: string) {

    }

    public addCard(card: CardInDeck): void {
        this.updateCard(
            card.deckCard.card.item.id!,
            card.deckCard.card.item.name!,
            card.deckCard.card.item.data!.magic!.set!.name!,
            card.deckCard.card.item.data!.magic!.set!.code!,
            card.deckCard.card.item.data!.magic!.setIndex!,
            card.deckCard.count,
            card.deckCard.specialCount);
    }

    public updateCard(itemId: string, name: string, setName: string, setCode: string, setIndex: string, count: number, specialCount: number): void {
        var matchingCard: MatchingCardInDeck | undefined;

        for (let i = 0; i < this.cards.length; ++i) {
            if (this.cards[i].itemId == itemId) {
                matchingCard = this.cards[i];
                break;
            }
        }

        if (!matchingCard) {
            matchingCard = new MatchingCardInDeck(
                itemId, 
                name, 
                setName,
                setCode,
                setIndex);

            this.cards.push(matchingCard);
        }

        matchingCard.count = count;
        matchingCard.specialCount = specialCount;
    }
}

export class MatchingCardInDeck {
    public count: number = 0;
    public specialCount: number = 0;

    public constructor(
        public itemId: string, 
        public cardName: string, 
        public setName: string, 
        public setCode: string, 
        public setIndex: string) {

    }
}